import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable react/require-default-props */
import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { formatListValue } from '@/shared/components/FormattedListValue';
import PopoverStickOnHover from '@/shared/components/PopoverStickOnHover';
import { CommentIcon, PaperclipIcon } from '@/shared/components/Icons';
import { useCreateTaskTimeRegistration, } from '@/shared/hooks/task/registration/useCreateTaskTimeRegistration';
import { defaultItemType, getTaskColor, states, TaskStatus, Method, } from '@/modules/tasks/taskboard/kanban/constants';
import authService, { functionalities, privileges } from '@/shared/services/AuthService';
import taskMessages from '@/modules/tasks/taskMessages';
import EditableUserSearch from '@/shared/components/editable/EditableUserSearch';
import { useDataContext } from '@/shared/providers/DataProvider';
import { useUpdateAssignedUser } from '@/shared/hooks/task/useUpdateAssignedUser';
import { useGetPlannedTaskRemark } from '@/shared/hooks/task/useGetPlannedTaskRemark';
import { ModalType, useModalContext } from '@/shared/providers/ModalProvider';
import { useUpdateTaskStatus } from '@/shared/hooks/task/useUpdateTaskStatus';
import { openTaskDocumentationTab, } from '@/shared/utils/openTaskDocumentationTab';
import { useTaskTimerContext } from '@/shared/providers/TaskTimerProvider';
import { useDeletePlannedTask } from '@/shared/hooks/task/useDeletePlannedTask';
import { useGetTaskAttachments } from '@/shared/hooks/task/useGetTaskAttachments';
import EditableDatePicker from '@/shared/components/editable/EditableDatePicker';
import ToastrService from '@/shared/services/ToastrService';
import appMessages from '@/shared/messages/appMessages';
import { useUpdateTaskDeadline } from '@/shared/hooks/task/useUpdateTaskDeadline';
import { useUpdateTaskExecutionDate } from '@/shared/hooks/task/useUpdateTaskExecutionDate';
import HasFeature from '@/shared/components/security/HasFeature';
import FormattedDateShort from '@/shared/components/FormattedDateShort';
import TaskDropdownButton from './TaskDropdownButton';
import { getUser } from '../helper';
import TaskAttachmentsPopover from '../../../TaskAttachmentsPopover';
import { useGetTaskDocumentation } from '@/shared/hooks/task/useGetTaskDocumentation';
import AuthService from '@/shared/services/AuthService';
import { useHasTaskflowBilling } from '@/shared/providers/TaskflowBillingProvider';
const extracted = (shareItem, subTask, toggleBlockItem, toggleInapplicable, toggleRejected, deleteItem, state, intl) => {
    const canUpdateTask = AuthService.hasPermission({
        functionality: functionalities.PlannedTask,
        privilege: privileges.Update,
    });
    const menuItems = [
        {
            title: intl.formatMessage(taskMessages.Share),
            icon: _jsx("i", { className: "fa fa-fw fa-share-alt" }),
            onSelect: () => {
                shareItem(subTask);
            },
        },
        canUpdateTask &&
            !subTask.isBlocked &&
            state.status !== 2 && {
            title: intl.formatMessage(taskMessages.Block),
            icon: (_jsxs("span", Object.assign({ className: "fa-stack fa-sm" }, { children: [_jsx("i", { className: "fa fa-fw fa-circle fa-stack-2x fa-inverse" }), _jsx("i", { className: "fa fa-fw fa-minus-circle fa-stack-2x text-danger" })] }))),
            onSelect: () => {
                toggleBlockItem(subTask);
            },
        },
        canUpdateTask &&
            subTask.isBlocked && {
            title: intl.formatMessage(taskMessages.Unblock),
            icon: (_jsxs("span", Object.assign({ className: "fa-stack fa-sm" }, { children: [_jsx("i", { className: "fa fa-fw fa-circle fa-stack-2x text-primary" }), _jsx("i", { className: "fa fa-fw fa-arrow-up fa-stack-1x fa-inverse", style: { top: '-1px', left: '-0.5px' } })] }))),
            onSelect: () => {
                toggleBlockItem(subTask);
            },
        },
        canUpdateTask &&
            !subTask.inapplicable && {
            title: intl.formatMessage(taskMessages.MarkAsInapplicable),
            icon: _jsx("i", { className: "fa fa-fw fa-user-times" }),
            onSelect: () => {
                toggleInapplicable(subTask);
            },
        },
        canUpdateTask &&
            subTask.inapplicable && {
            title: intl.formatMessage(taskMessages.MarkAsApplicable),
            icon: _jsx("i", { className: "fa fa-fw fa-check-circle text-primary" }),
            onSelect: () => {
                toggleInapplicable(subTask);
            },
        },
        canUpdateTask &&
            !subTask.rejected && {
            title: intl.formatMessage(taskMessages.MarkAsRejected),
            icon: _jsx("i", { className: "fa fa-fw fa-ban" }),
            onSelect: () => {
                toggleRejected(subTask);
            },
        },
        canUpdateTask &&
            subTask.rejected && {
            title: intl.formatMessage(taskMessages.MarkAsNotRejected),
            icon: _jsx("i", { className: "fa fa-fw fa-check-circle text-primary" }),
            onSelect: () => {
                toggleRejected(subTask);
            },
        },
        AuthService.hasPermission({
            functionality: functionalities.PlannedTask,
            privilege: privileges.Delete,
        }) && {
            title: intl.formatMessage(taskMessages.Delete),
            icon: (_jsx("span", Object.assign({ className: "fa-stack fa-sm" }, { children: _jsx("i", { className: "fa fa-trash-o fa-fw fa-stack-2x" }) }))),
            onSelect: () => {
                deleteItem(subTask);
            },
        },
    ];
    return menuItems.filter(f => !!f);
};
const SubTaskComponent = ({ task, subTask, shareItem, openTaskDetails, reloadColumns, isDisabled = false, isDragging, }) => {
    var _a, _b, _c;
    const { setShowModalInfo } = useModalContext();
    const { users } = useDataContext();
    const { updateAssignedUser } = useUpdateAssignedUser();
    const { updateTaskStatus } = useUpdateTaskStatus();
    const { deletePlannedTask } = useDeletePlannedTask();
    const { updateTaskDeadline } = useUpdateTaskDeadline();
    const { updateTaskExecutionDate } = useUpdateTaskExecutionDate();
    const hasTaskflowBilling = useHasTaskflowBilling(task === null || task === void 0 ? void 0 : task.invoiceScheduleId, subTask === null || subTask === void 0 ? void 0 : subTask.templateId);
    const { startStopTimer, isTimerRunningForSubTaskId, processTimeRegistrationResult, } = useTaskTimerContext();
    const intl = useIntl();
    const { createTaskTimeRegistration } = useCreateTaskTimeRegistration();
    const [loadDocumentation, setLoadDocumentation] = useState(false);
    const [loadRemark, setLoadRemark] = useState(false);
    const [loadAttachments, setLoadAttachments] = useState(false);
    const { data: remark, isLoading: loadingRemark } = useGetPlannedTaskRemark(subTask.id, 1, false, loadRemark);
    const { data: documentation, isLoading: loadingDocumentation } = useGetTaskDocumentation(subTask.templateId, loadDocumentation);
    const { data: attachments, isLoading: loadingAttachments } = useGetTaskAttachments(subTask.id, 1, loadAttachments);
    const isTimerRunning = useMemo(() => isTimerRunningForSubTaskId === subTask.id, [
        subTask.id,
        isTimerRunningForSubTaskId,
    ]);
    const { defaultCompany } = useDataContext();
    const retrieveRemark = () => {
        setLoadRemark(true);
    };
    const retrieveDocumentation = () => {
        setLoadDocumentation(true);
    };
    const getDisplayName = useCallback((item) => {
        var _a;
        return (_a = item.name) !== null && _a !== void 0 ? _a : formatListValue({
            list: 'tasktemplate',
            id: item.templateId,
            intl,
            fallback: item.templateName,
        });
    }, [intl]);
    const openDocumentationInNewTab = useCallback(() => {
        var _a;
        const mainItem = {
            id: task.id,
            templateId: task.templateId,
            hasDocumentation: task.hasDocumentation,
            name: getDisplayName(task),
        };
        const subItems = ((_a = task.children) !== null && _a !== void 0 ? _a : []).map(child => ({
            id: child.id,
            templateId: child.templateId,
            hasDocumentation: child.hasDocumentation,
            name: getDisplayName(child),
        }));
        openTaskDocumentationTab(mainItem, subItems, subTask.id);
    }, [task, subTask.id, getDisplayName]);
    const retrieveAttachments = () => {
        setLoadAttachments(true);
    };
    const openTimeRegistrationDialog = (s) => {
        s.relation = task.relation;
        s.mainTaskId = task.id;
        s.mainTemplateId = task.templateId;
        s.assignmentTemplateId = task.assignmentTemplateId;
        setShowModalInfo({ modalType: ModalType.TimeRegistration, task: s });
    };
    const openMailDialog = (s) => {
        setShowModalInfo({
            modalType: ModalType.TaskMail,
            task: s,
            onSave: async () => {
                await updateTaskStatus({
                    id: s.id,
                    status: TaskStatus.Done,
                    isBlocked: s.isBlocked,
                    inapplicable: s.inapplicable,
                    rejected: s.rejected,
                });
                if (defaultCompany.taskTimeRegistrationMethod === Method.AutomaticRegistration) {
                    if (s.defaultDuration && s.defaultDuration > 0) {
                        const taskTimeRegistration = {
                            plannedTaskId: s.id,
                            plannedTaskTemplateId: s.templateId,
                            plannedTaskName: s.name,
                            invoiceStreamId: s.invoiceStreamId,
                            relationId: s.relationId,
                            resourceId: s.resourceId,
                            durationMinutes: s.defaultDuration,
                            mainPlannedTaskId: task.id,
                            mainTaskTemplateId: task.templateId,
                            mainPlannedTaskName: task.name,
                        };
                        const timeRegistrationResult = await createTaskTimeRegistration(taskTimeRegistration);
                        await processTimeRegistrationResult(timeRegistrationResult);
                    }
                    else {
                        ToastrService.info(intl.formatMessage(appMessages.msgNoRegistrationMadeDefaultTimeZero));
                    }
                }
                else if (defaultCompany.taskTimeRegistrationMethod === Method.OpenFormOnDone) {
                    openTimeRegistrationDialog(s);
                }
                reloadColumns();
            },
        });
    };
    const onStartStopTimer = async () => {
        if (!isDisabled) {
            await startStopTimer(task, subTask);
        }
    };
    const state = states[subTask.status];
    const getTaskTypeIcon = () => {
        switch (subTask.type) {
            case 0:
                return _jsx("i", { className: "fa fa-user task-type-icon", style: { fontSize: '16px' } });
            case 1:
                return _jsx("i", { className: "fa fa-hourglass task-type-icon" });
            case 2:
                return (_jsx("i", { className: "fa fa-question task-type-icon", title: intl.formatMessage(taskMessages.TimeRegistrationIsNotPossibleTaskTypeCondition) }));
            case 3:
                if (state.status <= 1) {
                    return (_jsx("i", { className: "fa fa-envelope action-icon task-type-icon clickable", onClick: () => !isDisabled && openMailDialog(subTask), title: intl.formatMessage(taskMessages.SendMail) }));
                }
                if (state.status === 2) {
                    return (_jsx("a", Object.assign({ onClick: () => openTaskDetails(subTask), title: intl.formatMessage(taskMessages.OpenMailHistory) }, { children: _jsx("i", { className: "fa fa-envelope" }) })));
                }
                return null;
            case 4:
                if (state.status <= 1) {
                    return (_jsxs("span", Object.assign({ onClick: () => !isDisabled && openMailDialog(subTask), title: intl.formatMessage(taskMessages.AutomaticMailTooltip), className: "fa-stack fa-sm action-icon task-type-icon clickable" }, { children: [_jsx("i", { className: "fa fa-calendar fa-stack-2x text-blue" }), _jsx("i", { className: "fa fa-envelope fa-stack-15x ", style: { left: '2px', top: '5px' } })] })));
                }
                if (state.status === 2) {
                    return (_jsx("a", Object.assign({ onClick: () => openTaskDetails(subTask), title: intl.formatMessage(taskMessages.OpenMailHistory) }, { children: _jsx("i", { className: "fa fa-envelope" }) })));
                }
                return null;
            default:
                return null;
        }
    };
    const toggleBlockItem = useCallback((s) => {
        updateTaskStatus({
            id: s.id,
            status: s.status,
            isBlocked: !s.isBlocked,
            inapplicable: s.inapplicable,
            rejected: s.rejected,
        });
    }, [updateTaskStatus]);
    const toggleInapplicable = useCallback((s) => {
        updateTaskStatus({
            id: s.id,
            status: s.status,
            isBlocked: s.isBlocked,
            inapplicable: !s.inapplicable,
            rejected: s.rejected,
        });
    }, [updateTaskStatus]);
    const toggleRejected = useCallback((s) => {
        updateTaskStatus({
            id: s.id,
            status: s.status,
            isBlocked: s.isBlocked,
            inapplicable: s.inapplicable,
            rejected: !s.rejected,
        });
    }, [updateTaskStatus]);
    const onDeleteTask = useCallback((s) => {
        setShowModalInfo({
            modalType: ModalType.YesNo,
            title: intl.formatMessage(taskMessages.DeleteTask),
            message: intl.formatMessage(taskMessages.DeleteTaskConfirmationMsg),
            onSave: async () => {
                deletePlannedTask({ taskId: s.id, taskLevel: s.taskLevel });
            },
        });
    }, [setShowModalInfo, deletePlannedTask, intl]);
    const menuItems = useMemo(() => extracted(shareItem, subTask, toggleBlockItem, toggleInapplicable, toggleRejected, onDeleteTask, state, intl), [
        shareItem,
        subTask,
        toggleBlockItem,
        toggleInapplicable,
        toggleRejected,
        onDeleteTask,
        state,
        intl,
    ]);
    const selectedUser = getUser((_a = subTask.userId) !== null && _a !== void 0 ? _a : task.userId, users);
    const canUpdateTask = useMemo(() => authService.hasPermission({
        functionality: functionalities.PlannedTask,
        privilege: privileges.Update,
    }), []);
    const taskColor = getTaskColor(subTask);
    const remarkPopover = (_jsxs("span", Object.assign({ id: "popover-trigger-hover-focus" }, { children: [loadingRemark ? _jsx("i", { className: "fa fa-spin fa-spinner" }) : null, _jsx("span", Object.assign({ style: { whiteSpace: 'pre-line' } }, { children: remark === null || remark === void 0 ? void 0 : remark.remark }))] })));
    const documentationPopover = (_jsxs("span", Object.assign({ id: "popover-trigger-hover-focus" }, { children: [loadingDocumentation ? _jsx("i", { className: "fa fa-spin fa-spinner" }) : null, _jsx("div", { dangerouslySetInnerHTML: { __html: documentation === null || documentation === void 0 ? void 0 : documentation.documentation } })] })));
    const showTimerRunning = useMemo(() => defaultCompany.taskTimeRegistrationMethod === 2 && isTimerRunning, [defaultCompany.taskTimeRegistrationMethod, isTimerRunning]);
    const onChangeUser = (user) => {
        var _a;
        const userId = (_a = user === null || user === void 0 ? void 0 : user.id) !== null && _a !== void 0 ? _a : null;
        updateAssignedUser({
            taskId: subTask.id,
            userId,
            taskLevel: subTask.taskLevel,
        });
    };
    const onUpdateDeadline = (deadline) => {
        updateTaskDeadline({
            id: subTask.id,
            deadline: deadline || null,
            taskLevel: subTask.taskLevel,
        });
    };
    const onUpdateExecutionDate = (executionDate) => {
        updateTaskExecutionDate({
            id: subTask.id,
            executionDate: executionDate || null,
        });
    };
    return (_jsx("div", Object.assign({ className: "dskb-item-container", id: `task_${subTask.id}` }, { children: _jsxs("div", Object.assign({ className: "dskb-item-element", style: {
                backgroundColor: taskColor.backgroundColor,
            } }, { children: [_jsx("div", { className: classNames({ 'tr-running-n': showTimerRunning }) }), _jsx("div", { className: "dskb-gripper", style: {
                        backgroundColor: (_b = taskColor.color) !== null && _b !== void 0 ? _b : defaultItemType.color,
                    } }), _jsx("div", Object.assign({ className: "dskb-count" }, { children: _jsx("p", { children: subTask.priority }) })), _jsxs("div", Object.assign({ style: {
                        flex: 1,
                        marginTop: 5,
                        marginBottom: 5,
                        zIndex: showTimerRunning ? 1 : undefined,
                    } }, { children: [_jsx("a", Object.assign({ id: `subtask-item-${subTask.name}`, className: classNames('name-property', (subTask.inapplicable || subTask.rejected) && 'strike'), onClick: () => openTaskDetails(subTask) }, { children: (_c = subTask.name) !== null && _c !== void 0 ? _c : formatListValue({
                                list: 'tasktemplate',
                                id: subTask.templateId,
                                intl,
                                fallback: subTask.templateName,
                            }) })), _jsx(HasFeature, Object.assign({ feature: "CapacityPlanning" }, { children: _jsxs("div", Object.assign({ style: { display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' } }, { children: [_jsx("i", { className: "fa fa-calendar-check-o", title: intl.formatMessage(taskMessages.ExecutionDate), style: { marginRight: 5 } }), _jsx(EditableDatePicker, { showClearDate: true, value: subTask.executionDate ? moment.utc(subTask.executionDate) : null, nullDisplayValue: intl.formatMessage(taskMessages.NoExecutionDate), onChange: executionDate => onUpdateExecutionDate(executionDate), readOnly: !canUpdateTask, showTodayButton: true })] })) })), _jsxs("div", Object.assign({ style: { display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' } }, { children: [_jsx("i", { className: "fa fa-calendar", title: intl.formatMessage(taskMessages.Deadline), style: { marginRight: 5 } }), subTask.status !== 2 ? (_jsx(EditableDatePicker, { showClearDate: true, value: subTask.deadline ? moment.utc(subTask.deadline) : null, nullDisplayValue: intl.formatMessage(taskMessages.NoDeadline), onChange: deadline => onUpdateDeadline(deadline), readOnly: !canUpdateTask })) : subTask.deadline ? (_jsx(FormattedDateShort, { value: moment.utc(subTask.deadline).toDate() })) : (_jsx("span", { children: intl.formatMessage(taskMessages.NoDeadline) })), state.status === 2 && subTask.completedDate && (_jsxs(_Fragment, { children: [_jsx("i", { className: "fa fa-check", style: { marginRight: 5, marginLeft: 10 } }), _jsx("span", { children: intl.formatDate(subTask.completedDate) })] }))] }))] })), _jsxs("div", Object.assign({ style: {
                        display: 'flex',
                        flexDirection: 'column',
                        margin: 5,
                        zIndex: showTimerRunning ? 1 : undefined,
                    } }, { children: [_jsxs("div", Object.assign({ style: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' } }, { children: [subTask.isBlocked && (_jsx("i", { className: "fa fa-minus-circle text-danger blocked-icon", title: intl.formatMessage(taskMessages.Blocked), style: { marginRight: 7 } })), hasTaskflowBilling ? (_jsx("span", Object.assign({ style: { marginRight: 7 } }, { children: _jsx("i", { className: "fa fa-euro text-danger", title: intl.formatMessage(taskMessages.SubtaskWithTaskflowBilling) }) }))) : null, subTask.hasAttachments && (_jsx("span", Object.assign({ style: { marginRight: 7, marginTop: '4px' } }, { children: _jsx(PopoverStickOnHover, Object.assign({ trigger: ['hover', 'focus'], placement: "top", component: _jsx(TaskAttachmentsPopover, { attachments: attachments, loadingAttachments: loadingAttachments }), shouldUpdatePosition: true, onMouseEnter: retrieveAttachments, onClick: () => openTaskDetails(subTask) }, { children: _jsx("span", { children: _jsx(PaperclipIcon, {}) }) })) }))), subTask.hasRemark && (_jsx("span", Object.assign({ style: { marginRight: 7, marginTop: '4px' } }, { children: _jsx(PopoverStickOnHover, Object.assign({ trigger: ['hover', 'focus'], placement: "top", component: remarkPopover, shouldUpdatePosition: true, onMouseEnter: retrieveRemark, onClick: () => openTaskDetails(subTask) }, { children: _jsx("span", { children: _jsx(CommentIcon, {}) }) })) }))), ((subTask.type !== 2 && defaultCompany.taskTimeRegistrationMethod !== 2) ||
                                    (subTask.type !== 2 &&
                                        defaultCompany.taskTimeRegistrationMethod === 2 &&
                                        state.status === 2)) &&
                                    !isTimerRunning && (_jsx("a", Object.assign({ onClick: () => !isDisabled && openTimeRegistrationDialog(subTask), style: { marginRight: 7 } }, { children: _jsx("i", { className: "fa fa-clock-o", title: intl.formatMessage(taskMessages.CreateTimeRegistration) }) }))), subTask.type !== 3 &&
                                    subTask.type !== 4 &&
                                    subTask.type !== 1 &&
                                    (isTimerRunning ||
                                        (state.status === 1 && defaultCompany.taskTimeRegistrationMethod === 2)) && (_jsx("a", Object.assign({ onClick: onStartStopTimer, style: { marginRight: 7 } }, { children: isTimerRunning ? (_jsx("i", { className: "fa fa-pause-circle", title: intl.formatMessage(taskMessages.PauseTimer) })) : (_jsx("i", { className: "fa fa-play-circle", title: intl.formatMessage(taskMessages.StartTimer) })) }))), subTask.hasDocumentation && (_jsx("span", Object.assign({ style: { marginRight: 7 } }, { children: _jsx(PopoverStickOnHover, Object.assign({ trigger: ['hover', 'focus'], placement: state.status === 2 ? 'left' : 'bottom', component: documentationPopover, shouldUpdatePosition: true, onMouseEnter: retrieveDocumentation, onClick: openDocumentationInNewTab }, { children: _jsx("i", { className: "fa fa-book clickable" }) })) }))), getTaskTypeIcon(), (menuItems === null || menuItems === void 0 ? void 0 : menuItems.length) > 0 && (_jsx("div", Object.assign({ style: { display: 'flex', marginLeft: 7 } }, { children: _jsx(TaskDropdownButton, { disabled: false, menuItems: menuItems }) })))] })), _jsx("div", Object.assign({ style: { flex: 1 } }, { children: _jsx(EditableUserSearch, { onChange: user => onChangeUser(user), value: selectedUser, className: classNames('bold-text', !subTask.userId && 'italic-text'), submitOnChange: true, withUnassigned: true, unassignedId: null }) }))] }))] })) }), `${task.id}${subTask.id}${isTimerRunning}`));
};
export default SubTaskComponent;
